<template>
  <div class="minimum-quantity-rule">
    <div class="rule-section">
      <p class="rule-section-title text-white">Quantity requirements</p>

      <div class="row">
        <div class="col-12">
          <base-input label="Requirement type">
            <segmented-toggle
              variant="pills"
              size="sm"
              :options="modeOptions"
              :value="mode"
              @input="onModeChange"
            ></segmented-toggle>
          </base-input>
        </div>
      </div>

      <div class="row">
        <div class="col-md-4" v-if="mode === 'minimum' || mode === 'range'">
          <base-input label="Minimum quantity">
            <el-input-number
              v-model="localConfig.min_quantity"
              :min="1"
              :max="localConfig.max_quantity || 999"
              @change="emitUpdate"
            ></el-input-number>
          </base-input>
        </div>
        <div class="col-md-4" v-if="mode === 'maximum' || mode === 'range'">
          <base-input label="Maximum quantity">
            <el-input-number
              v-model="localConfig.max_quantity"
              :min="mode === 'range' ? (localConfig.min_quantity || 1) : 1"
              :max="999"
              @change="emitUpdate"
            ></el-input-number>
          </base-input>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <base-input label="Applies to">
            <el-select
              v-model="localConfig.applies_to"
              class="select-primary"
              placeholder="Select what to count"
              @change="emitUpdate"
            >
              <el-option
                class="select-primary"
                value="total_participants"
                label="Total participants (for bookings)"
              >
                <div>
                  <strong>Total participants</strong>
                  <div class="text-muted small">Count group size across all bookings</div>
                </div>
              </el-option>
              <el-option
                class="select-primary"
                value="line_items"
                label="Line items in cart"
              >
                <div>
                  <strong>Line items in cart</strong>
                  <div class="text-muted small">Count total number of items in cart</div>
                </div>
              </el-option>
              <el-option
                class="select-primary"
                value="unique_items"
                label="Unique items (different products)"
              >
                <div>
                  <strong>Unique items</strong>
                  <div class="text-muted small">Count number of different products (ignores quantity)</div>
                </div>
              </el-option>
            </el-select>
          </base-input>
        </div>
      </div>

      <!-- Helper text explaining behavior based on value type -->
      <div class="row mt-2" v-if="localConfig.applies_to === 'total_participants'">
        <div class="col">
          <p class="small mb-0 helper-text text-white">
            <i class="tim-icons el-icon-info mr-1"></i>
            <span v-if="valueType === 0">
              <strong>Per transaction:</strong> The total participants across all bookings in the cart must fall within these limits. If the cart qualifies, all bookings receive the discount.
            </span>
            <span v-else-if="valueType === 1">
              <strong>Per line item:</strong> Each booking is evaluated individually. Only bookings whose participant count falls within these limits will receive the discount.
            </span>
            <span v-else>
              <strong>Per quantity:</strong> The total participants across all bookings must fall within these limits. Discount applies per participant.
            </span>
          </p>
        </div>
      </div>
    </div>

    <!-- Error message (commented out for now)
    <div class="row">
      <div class="col-md-12">
        <base-input>
          <label>
            Error message (optional)
            <el-popover
              trigger="hover"
              placement="right"
            >
              <div>
                <div class="popover-body">Shown to customer when this rule is not met</div>
              </div>
              <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
            </el-popover>
          </label>
          <el-input
            v-model="localConfig.error_message"
            type="textarea"
            :rows="2"
            maxlength="200"
            show-word-limit
            placeholder="e.g., This promo code requires a group of 4 or more"
            @input="emitUpdate"
          ></el-input>
        </base-input>
      </div>
    </div>
    -->
  </div>
</template>

<script>
import { BaseInput } from 'src/components';

export default {
  name: 'MinimumQuantityRule',
  components: {
    BaseInput
  },
  props: {
    ruleConfig: {
      type: Object,
      required: true
    },
    valueType: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      mode: 'minimum',
      modeOptions: [
        { value: 'minimum', label: 'Minimum' },
        { value: 'maximum', label: 'Maximum' },
        { value: 'range', label: 'Range' }
      ],
      localConfig: {
        min_quantity: 1,
        max_quantity: null,
        applies_to: 'total_participants',
        error_message: ''
      }
    };
  },
  watch: {
    ruleConfig: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (newVal) {
          this.localConfig = { ...this.localConfig, ...newVal };
          this.mode = this.deriveMode(this.localConfig);
        }
      }
    }
  },
  methods: {
    deriveMode(config) {
      const hasMin = config.min_quantity != null;
      const hasMax = config.max_quantity != null;
      if (hasMin && hasMax) return 'range';
      if (hasMax) return 'maximum';
      return 'minimum';
    },
    onModeChange(mode) {
      this.mode = mode;
      if (mode === 'minimum') {
        if (this.localConfig.min_quantity == null) this.localConfig.min_quantity = 1;
        this.localConfig.max_quantity = null;
      } else if (mode === 'maximum') {
        if (this.localConfig.max_quantity == null) this.localConfig.max_quantity = 1;
        this.localConfig.min_quantity = null;
      } else {
        // range: keep both bounds, defaulting any missing one
        if (this.localConfig.min_quantity == null) this.localConfig.min_quantity = 1;
        if (this.localConfig.max_quantity == null) this.localConfig.max_quantity = this.localConfig.min_quantity || 1;
      }
      this.emitUpdate();
    },
    emitUpdate() {
      this.$emit('update', { ...this.localConfig });
    }
  }
};
</script>

<style scoped>
/* Section styling */
.rule-section {
  padding: 1rem 1.25rem;
  background: rgba(128, 128, 128, 0.08);
  border-radius: 0.375rem;
  border-left: 3px solid #e14eca;
  margin-bottom: 1rem;
}

.rule-section-title {
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 1rem 0;
  color: #fff;
}

/* Improve label legibility */
.minimum-quantity-rule >>> label {
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
}

.minimum-quantity-rule >>> .form-group label {
  color: rgba(255, 255, 255, 0.8);
}

/* Select input text */
.minimum-quantity-rule >>> .el-input__inner,
.minimum-quantity-rule >>> .el-textarea__inner {
  color: #fff;
}

/* Helper text */
.minimum-quantity-rule .text-muted {
  color: rgba(255, 255, 255, 0.5) !important;
}

.helper-text {
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.4;
}
</style>
